/**
 * #1247 — one submission's receipt, as the Kalshi/Polymarket ticket says it:
 * a short state word and the order it names, on one line by Place Order.
 *
 * The state word follows the order. It starts from the submit's own receipt
 * and moves with each later read of the same intent (`followed`, the
 * portfolio client's saved evidence): Awaiting acceptance → Resting →
 * Partly filled → Filled, or Canceled / Expired / Rejected. "Unconfirmed"
 * stays only while nothing has confirmed the order; a read that answers
 * replaces a lost lookup. Nothing here sends, retries or acknowledges.
 *
 * The visible word carries what the trader must act on (unconfirmed: check
 * before resending; rejected: the reason). The hover reference carries the
 * venue's question, the exact terms, the intent and the venue order id.
 */
import type { OrderResultSeverity } from '@/views/ValueDashboard/oscarResultPresentation';
import { usMarketTitle } from '@/lib/polymarketUs/marketLabel';
import { RECEIPT_LABEL } from '@/lib/polymarketUs/portfolioModel';
import { usOrderReason, usOrderReasonSentence, usReasonRepeatRefused } from '@/lib/polymarketUs/reason';
import { decimalUnits, durationLabel, formatUnits } from '@/lib/polymarketUs/ticketModel';
import type { UsDraft, UsLifecycle, UsOrderRow, UsReceipt } from '@/lib/polymarketUs/types';
import type { UsTicketResult } from './ticketAdapter';

/** What the trader should do next, per receipt status. Nothing here resends an order. */
export const RECEIPT_GUIDANCE: Record<UsReceipt['status'], string> = {
  ready: 'Recorded but not yet sent. Check its status in your orders before placing another.',
  aborted: 'Proven not sent. Review the ticket and place it again if you still want this order.',
  submitted: 'The venue acknowledged the order. Acceptance is not confirmed yet — check its status in your orders.',
  accepted: 'The venue accepted the order. Fills and remaining quantity appear in your orders.',
  rejected: 'Rejected by Polymarket US. Nothing was placed.',
  unknown: 'The result could not be confirmed. Do not place it again — check its status in your orders first.',
};

/** The lost-response guidance: no server intent is known yet. */
export const LOST_GUIDANCE = 'Check it in “Your saved US requests” before placing another order. This does not prove the order was not sent, and it will not be resent.';

const LIFECYCLE_GUIDANCE: Record<UsLifecycle['state'], string> = {
  pending: 'The venue has the order; acceptance is not confirmed yet.',
  open: 'Resting on Polymarket US. Fills and remaining quantity appear in your orders.',
  partial: 'Partly filled and still resting. Fills and remaining quantity appear in your orders.',
  filled: 'Filled.',
  canceled: 'Canceled. Nothing of it rests.',
  expired: 'Expired. Nothing of it rests.',
  replaced: 'Replaced; the venue evidence is still being reconciled. Check your orders.',
  rejected: 'Rejected by Polymarket US.',
  conflict: 'The venue evidence for this order conflicts. Check your orders before placing another.',
};

/** Lifecycle states after which the order cannot change again. */
export const US_TERMINAL_LIFECYCLE: readonly UsLifecycle['state'][] = ['filled', 'canceled', 'expired', 'rejected'];

export type UsReceiptView = {
  /** The submit's receipt status (`unconfirmed` when none), unchanged by later reads. */
  status: UsReceipt['status'] | 'unconfirmed';
  /** Where the order stands now: a lifecycle state once a read names one. */
  state: UsReceipt['status'] | UsLifecycle['state'] | 'unconfirmed';
  /** The visible state word(s). Short: it shares one line with the order. */
  word: string;
  severity: OrderResultSeverity;
  /** The order, K/P style: `1 Texans @ 1¢ (HOU @ JAX)`; `SELL …` for a sale. */
  attempt: string;
  /** The full next-step sentence, with a lost lookup's note when there is one. */
  guidance: string;
  /** The next step for the latest state alone (no lookup note). */
  nextStep: string;
  /** Hover reference: the venue's question, the exact terms and the ids. */
  reference: string;
  /** True once nothing about this order can change (no further reads). */
  terminal: boolean;
};

/** USD per contract (`0.4`) → cents text (`40`), exactly; unreadable text stays as is. */
export function usdToCentsText(usd: string): string {
  const n = decimalUnits(usd);
  return n === null ? usd : formatUnits(n * 100n);
}

function filledOf(lifecycle: UsLifecycle, ordered: string): string {
  return `${lifecycle.filledQuantity} of ${ordered}`;
}

const hasFill = (lifecycle: UsLifecycle) => (decimalUnits(lifecycle.filledQuantity) ?? 0n) > 0n;

function lifecycleWord(lifecycle: UsLifecycle, ordered: string): { word: string; severity: OrderResultSeverity } {
  switch (lifecycle.state) {
    case 'pending': return { word: 'Awaiting acceptance', severity: 'pending' };
    case 'open': return { word: 'Resting', severity: 'success' };
    case 'partial': return { word: `Partly filled ${filledOf(lifecycle, ordered)}`, severity: 'success' };
    case 'filled': return { word: 'Filled', severity: 'success' };
    case 'canceled': return { word: hasFill(lifecycle) ? `Canceled · ${filledOf(lifecycle, ordered)} filled` : 'Canceled', severity: 'warning' };
    case 'expired': return { word: hasFill(lifecycle) ? `Expired · ${filledOf(lifecycle, ordered)} filled` : 'Expired', severity: 'warning' };
    case 'replaced': return { word: 'Replaced — reconciling', severity: 'pending' };
    case 'rejected': return { word: 'Rejected', severity: 'critical' };
    case 'conflict': return { word: 'Conflicting evidence — check your orders', severity: 'warning' };
  }
}

function receiptWord(receipt: UsReceipt): { word: string; severity: OrderResultSeverity } {
  switch (receipt.status) {
    case 'ready': return { word: 'Not sent yet — check your orders', severity: 'warning' };
    case 'aborted': return { word: `Not sent — ${usOrderReason(receipt.reason)}`, severity: 'warning' };
    case 'submitted': return { word: 'Awaiting acceptance', severity: 'pending' };
    // Accepted says nothing yet about fills: "placed", as K/P say it, until a read names the state.
    case 'accepted': return { word: 'Placed', severity: 'success' };
    case 'rejected': return { word: `Rejected — ${usOrderReason(receipt.reason)}`, severity: 'critical' };
    case 'unknown': return { word: 'Unconfirmed — check your orders before resending', severity: 'critical' };
  }
}

/**
 * The receipt as it stands. `followed` is the latest read of this submission's
 * intent (same intent id, else ignored); `outcomeName` the market's own name
 * for the outcome and `question` its question, as shown when it was sent.
 */
export function usReceiptView(
  result: UsTicketResult,
  draft: UsDraft,
  opts: { outcomeName?: string | null; question?: string | null; followed?: UsOrderRow | null } = {},
): UsReceiptView {
  const { receipt, requestKey, lookupReason } = result;
  const outcome = draft.target.outcome.toUpperCase();
  const name = opts.outcomeName?.trim() || outcome;
  const matchup = usMarketTitle({ marketSlug: draft.target.marketSlug });
  const attempt = `${draft.action === 'sell' ? 'SELL ' : ''}${draft.quantity} ${name} @ ${usdToCentsText(draft.outcomePrice)}¢`
    + (matchup !== draft.target.marketSlug ? ` (${matchup})` : '');
  const followed = receipt && opts.followed && opts.followed.intentId === receipt.intentId ? opts.followed : null;

  let state: UsReceiptView['state'];
  let word: string;
  let severity: OrderResultSeverity;
  let guidance: string;
  let terminal: boolean;
  if (followed?.lifecycle) {
    ({ word, severity } = lifecycleWord(followed.lifecycle, draft.quantity));
    state = followed.lifecycle.state;
    guidance = LIFECYCLE_GUIDANCE[state];
    terminal = US_TERMINAL_LIFECYCLE.includes(state);
  } else {
    const current: UsReceipt | null = followed?.receipt ?? receipt;
    if (current) {
      ({ word, severity } = receiptWord(current));
      state = current.status;
      const reason = 'reason' in current ? current.reason : null;
      guidance = current.status === 'aborted' && reason && usReasonRepeatRefused(reason)
        ? 'Proven not sent. Nothing was placed.' : RECEIPT_GUIDANCE[current.status];
      terminal = current.status === 'aborted' || current.status === 'rejected';
    } else {
      word = 'Unconfirmed — check saved requests before resending';
      severity = 'critical';
      state = 'unconfirmed';
      guidance = LOST_GUIDANCE;
      terminal = true; // no intent id: nothing to read
    }
  }
  const nextStep = guidance;
  // A lost lookup is said beside the recorded result until a read answers.
  if (lookupReason && !followed) {
    if (receipt) word = `${word} · lookup unconfirmed`;
    if (severity === 'success' || severity === 'pending') severity = 'warning';
    guidance = `${guidance} Latest lookup unconfirmed: ${usOrderReason(lookupReason)}.${receipt ? ' The recorded result is retained.' : ''}`;
  }

  const latest = followed?.receipt ?? receipt;
  const reasonCode = latest && 'reason' in latest ? latest.reason : null;
  const orderId = followed?.orderId ?? (receipt && 'orderId' in receipt ? receipt.orderId : null);
  const terms = `${outcome}${opts.outcomeName ? ` (${opts.outcomeName})` : ''} · ${draft.action.toUpperCase()} ${draft.quantity} contracts at $${draft.outcomePrice} per ${outcome}`
    + ` · ${durationLabel(draft.timeInForce)}${draft.goodTillTime ? ` until ${draft.goodTillTime}` : ''} · Post-only ${draft.postOnly ? 'on' : 'off'}`;
  const identity = receipt
    ? `Intent ${receipt.intentId} · Venue order ${orderId ?? 'unavailable'}`
    : `The server intent is not known yet. Your request was saved as request ${requestKey}; it is not an intent ID.`;
  const reference = [
    guidance,
    opts.question ?? draft.target.label,
    terms,
    ...(reasonCode ? [`Reason: ${usOrderReasonSentence(reasonCode, result.limit)} (${reasonCode})`] : []),
    identity,
    ...(receipt ? [`Sent as: ${RECEIPT_LABEL[receipt.status]}`] : []),
  ].join('\n');
  return { status: receipt?.status ?? 'unconfirmed', state, word, severity, attempt, guidance, nextStep, reference, terminal };
}
